import React, { useState, useEffect } from 'react';

const SearchableDropdown = ({ 
    options, 
    selectedValues, 
    onChange, 
    placeholder, 
    isMultiple = false, 
    displayKey = 'name',
    subDisplayKey = null,
    badgeKey = null,
    searchKeys = ['name', 'description', 'code'],
    disabled = false,
    className = "",
    compact = false  // When true: smaller fonts scoped to e.g. SOW table cells
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [search, setSearch] = useState('');
    const [focusedIndex, setFocusedIndex] = useState(-1);
    const containerRef = React.useRef(null);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (containerRef.current && !containerRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    useEffect(() => {
        setFocusedIndex(-1);
    }, [search, isOpen]);

    useEffect(() => {
        if (isOpen && focusedIndex >= 0) {
            const el = document.getElementById(`dropdown-option-${focusedIndex}`);
            if (el) el.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
        }
    }, [focusedIndex, isOpen]);

    const filteredOptions = (options || []).filter(opt => {
        const query = search.toLowerCase();
        return searchKeys.some(key => {
            const val = opt[key];
            if (val === undefined || val === null) return false;
            return String(val).toLowerCase().includes(query);
        });
    });

    const hasEmptyOption = !isMultiple;

    const handleKeyDown = (e) => {
        if (!isOpen) {
            if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                if (!disabled) setIsOpen(true);
            }
            return;
        }

        const optionsCount = filteredOptions.length + (hasEmptyOption ? 1 : 0);
        
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setFocusedIndex(prev => (prev < optionsCount - 1 ? prev + 1 : prev));
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setFocusedIndex(prev => (prev > 0 ? prev - 1 : 0));
        } else if (e.key === 'Enter') {
            e.preventDefault();
            if (hasEmptyOption && focusedIndex === 0) {
                onChange('');
                setIsOpen(false);
                setSearch('');
            } else {
                const actualIndex = hasEmptyOption ? focusedIndex - 1 : focusedIndex;
                if (actualIndex >= 0 && filteredOptions[actualIndex]) {
                    onChange(filteredOptions[actualIndex].id);
                    if (!isMultiple) {
                        setIsOpen(false);
                        setSearch('');
                    }
                }
            }
        } else if (e.key === 'Escape') {
            e.preventDefault();
            setIsOpen(false);
        }
    };

    // --- Trigger display logic ---
    let displayText = placeholder;
    let displaySubText = null;

    if (isMultiple) {
        // Multi-select: show count + first item preview when items are selected
        const vals = Array.isArray(selectedValues) ? selectedValues : (selectedValues ? [selectedValues] : []);
        if (vals.length > 0) {
            // Try to resolve first item name from options list
            const firstOpt = (options || []).find(o => o.id === vals[0] || String(o.id) === String(vals[0]));
            const firstName = firstOpt ? firstOpt[displayKey] : String(vals[0]);
            displayText = vals.length === 1
                ? firstName
                : `${firstName} +${vals.length - 1} more`;
        }
    } else if (selectedValues && selectedValues.length > 0) {
        const selectedOpt = (options || []).find(o => o.id === selectedValues[0] || String(o.id) === String(selectedValues[0]));
        if (selectedOpt) {
            displayText = selectedOpt[displayKey];
            if (subDisplayKey) displaySubText = selectedOpt[subDisplayKey];
        }
    }

    const hasSelection = isMultiple
        ? (Array.isArray(selectedValues) ? selectedValues.length > 0 : !!selectedValues)
        : (selectedValues && selectedValues.length > 0);

    // Font size classes based on compact prop
    const triggerTextClass = compact ? 'font-bold text-xs' : 'font-bold text-sm';
    const optionTextClass = compact ? 'text-sm' : 'text-base';
    const searchInputClass = compact ? 'text-[11px]' : 'text-xs';

    return (
        <div className={`relative w-full ${isOpen ? 'z-[90]' : 'z-10'} ${className}`} ref={containerRef} onKeyDown={handleKeyDown}>
            <div
                tabIndex={disabled ? -1 : 0}
                onClick={() => !disabled && setIsOpen(!isOpen)}
                className={`w-full px-5 py-2.5 bg-slate-100 border-2 rounded-2xl transition-all cursor-pointer flex items-center justify-between focus:outline-none focus:border-[#18216D] ${isOpen ? 'border-[#18216D] bg-white' : 'border-slate-200 hover:border-[#18216D]/30'} ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
                <div className="flex flex-col text-left mr-4 overflow-hidden">
                    <span className={`${triggerTextClass} leading-tight truncate ${hasSelection ? 'text-gray-900' : 'text-slate-400'}`}>
                        {displayText}
                    </span>
                    {displaySubText && (
                        <span className="text-[10px] text-slate-500 font-medium mt-1 truncate max-w-[300px] leading-tight">
                            {displaySubText}
                        </span>
                    )}
                </div>
                <svg className={`w-5 h-5 text-slate-400 transition-transform duration-200 shrink-0 ${isOpen ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M19 9l-7 7-7-7" />
                </svg>
            </div>

            {isOpen && (
                <div className="absolute z-[100] top-full mt-2 w-full bg-white border border-slate-100 shadow-2xl rounded-2xl overflow-hidden animate-in fade-in zoom-in-95 duration-200 flex flex-col">
                    <div className="p-3 border-b border-slate-50 bg-white sticky top-0 z-10 shrink-0">
                        <input
                            autoFocus
                            type="text"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder="Search..."
                            className={`w-full px-4 py-3 bg-slate-50 border border-slate-100 rounded-xl ${searchInputClass} font-bold focus:outline-none focus:ring-2 focus:ring-[#18216D]/20 transition-all`}
                        />
                    </div>
                    <div className="max-h-72 overflow-y-auto custom-scrollbar flex flex-col relative z-0">
                        {hasEmptyOption && (
                            <div 
                                id="dropdown-option-0"
                                onClick={() => { onChange(''); setIsOpen(false); setSearch(''); setFocusedIndex(-1); }}
                                className={`px-5 py-4 cursor-pointer transition-colors border-b border-slate-50 ${focusedIndex === 0 ? 'bg-slate-100' : 'hover:bg-slate-50'}`}
                            >
                                <span className={`font-bold text-slate-400 ${optionTextClass}`}>{placeholder}</span>
                            </div>
                        )}
                        {filteredOptions.length > 0 ? filteredOptions.map((opt, idx) => {
                            const isSelected = selectedValues.some(v => v === opt.id || String(v) === String(opt.id));
                            const actualIdx = hasEmptyOption ? idx + 1 : idx;
                            const isFocused = focusedIndex === actualIdx;
                            const optionId = `dropdown-option-${actualIdx}`;
                            
                            return (
                                <div
                                    key={opt.id}
                                    id={optionId}
                                    onClick={() => { 
                                        onChange(opt.id); 
                                        if (!isMultiple) { setIsOpen(false); setSearch(''); setFocusedIndex(-1); }
                                    }}
                                    className={`px-5 py-4 cursor-pointer transition-colors border-b border-slate-50 last:border-0 flex items-center justify-between ${isFocused ? 'bg-indigo-100/50' : (isSelected ? 'bg-indigo-50' : 'hover:bg-indigo-50/50')}`}
                                >
                                    <div className="flex flex-col mr-3">
                                        <div className={`font-black text-[#18216D] ${optionTextClass} mb-1 leading-tight flex items-center gap-2`}>
                                            {opt[displayKey]}
                                            {badgeKey && opt[badgeKey] && (
                                                <span className="px-2 py-0.5 bg-[#FFC425]/10 text-[#FFC425] text-[8px] uppercase tracking-widest font-black rounded-md">{opt[badgeKey]}</span>
                                            )}
                                        </div>
                                        {subDisplayKey && opt[subDisplayKey] && (
                                            <div className={`mt-1 font-medium text-slate-500 italic leading-relaxed ${compact ? 'text-[10px]' : 'text-[11px]'}`}>
                                                {opt[subDisplayKey]}
                                            </div>
                                        )}
                                    </div>
                                    {isMultiple && isSelected && (
                                        <svg className="w-5 h-5 text-indigo-600 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                                        </svg>
                                    )}
                                </div>
                            );
                        }) : (
                            <div className="px-5 py-8 text-center">
                                <span className={`font-bold text-slate-400 ${optionTextClass} uppercase tracking-widest`}>No matches found</span>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default SearchableDropdown;
